import { ApolloClient, ApolloQueryResult } from "@apollo/client"
import { exhaustiveGuard } from "@pathwright/ux/src/utils/typing"
import {
  ContextFragment,
  ContextualFeaturePermissionDocument,
  ContextualFeaturePermissionFragment,
  ContextualFeaturePermissionQuery,
  ContextualFeaturePermissionQueryResult,
  ContextualFeaturePermissionQueryVariables,
  ContextualFeaturePermissionsDocument,
  ContextualFeaturePermissionsQuery,
  ContextualFeaturePermissionsQueryResult,
  ContextualFeaturePermissionsQueryVariables,
  FeatureAction,
  FeatureKey,
  FeaturePermission,
  useContextualFeaturePermissionQuery,
  useContextualFeaturePermissionsQuery
} from "@pathwright/graphql-client/src/generated"
import { getApolloClient } from "@pathwright/features/src/modules/pathwright/PathwrightClient"
import useHomeActiveEntries from "../../home/useHomeActiveEntries"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import {
  canAdministrate,
  canEditLibrary,
  hasSchoolStaffAccess
} from "../../user/permissions"

// Fetching all VIEW CFPs.
const unifiedHomeViewVariables: ContextualFeaturePermissionsQueryVariables = {
  featureKeys: {
    in: [FeatureKey.HomeScreenUnified]
  },
  featureAction: FeatureAction.HomeScreenView
}

// Fetching singular PREVIEW CFP.
const unifiedHomePreviewVariables: ContextualFeaturePermissionQueryVariables = {
  featureKey: FeatureKey.HomeScreenPreview,
  featureAction: FeatureAction.View
}

// Generally named permission fields, possibly for reuse for other features that
// follow the same view/preview permission pattern.
type Permissions = {
  // Whether the user can view the feature.
  canView: null | boolean
  // Whether the user should be presented with the option to opt in/out of viewing the feature.
  canPreview: null | boolean
}

type GetPermissionsReturn = Permissions & {
  schoolScopedCFP: ContextualFeaturePermissionFragment | null
  userScopedCFP: ContextualFeaturePermissionFragment | null
  previewCFP: ContextualFeaturePermissionFragment | null
}

// Once we have the view and preview feature permissions query results, we can check
// if the user can view and preview the unified home screen.
// A null return indicates the permissions haven't loaded yet.
export function getPermissions(
  featureViewPermQuery:
    | ContextualFeaturePermissionsQueryResult
    | ApolloQueryResult<ContextualFeaturePermissionsQuery>,
  featurePreviewPermQuery:
    | ContextualFeaturePermissionQueryResult
    | ApolloQueryResult<ContextualFeaturePermissionQuery>,
  pwContext: ContextFragment
): GetPermissionsReturn {
  const viewCFPs = featureViewPermQuery.data?.contextualFeaturePermissions
  const previewCFP = featurePreviewPermQuery.data?.contextualFeaturePermission

  if (!featureViewPermQuery.data || !featurePreviewPermQuery.data) {
    // Data hasn't loaded yet
    return {
      canView: null,
      canPreview: null,
      schoolScopedCFP: null,
      userScopedCFP: null,
      previewCFP: null
    }
  }

  // When no CFPs exist, default to allowing view but not preview.
  // That said, a view "CFP" should always exist, since it's a default feature.
  if (!viewCFPs?.length) {
    return {
      canView: true,
      canPreview: false,
      schoolScopedCFP: null,
      userScopedCFP: null,
      previewCFP: null
    }
  }

  const schoolScopedCFP = viewCFPs.find(
    (cfp) => !cfp.user_id && !cfp.cohort_id && !cfp.resource_id
  )
  const userScopedCFP = viewCFPs.find((cfp) => cfp.user_id === pwContext.me?.id)

  let hasPreviewPermission = false

  if (previewCFP) {
    // TODO: this should be in a util that can scope to the CFP context (school, cohort, resource, user).
    switch (previewCFP.permission_in_context) {
      case FeaturePermission.Everyone:
        // Technically school members only?
        hasPreviewPermission = true
        break
      case FeaturePermission.StaffOnly:
        hasPreviewPermission = hasSchoolStaffAccess(pwContext)
        break
      case FeaturePermission.SchoolStaffOnly:
        hasPreviewPermission =
          canAdministrate(pwContext) || canEditLibrary(pwContext)
        break
      case FeaturePermission.AdminOnly:
        hasPreviewPermission = canAdministrate(pwContext)
        break
      case FeaturePermission.Disabled:
        hasPreviewPermission = false
        break
      case FeaturePermission.SuperuserOnly:
        hasPreviewPermission =
          pwContext.me?.permissions?.has_pathwright_staff_access ?? false
        break
      default:
        exhaustiveGuard(previewCFP.permission_in_context)
    }
  }

  // Logic for preview permission:
  // Show IF Preview CFP includes target User via user's permission level AND View CFP is NOT set to EVERYONE
  const canPreview = Boolean(
    hasPreviewPermission &&
      schoolScopedCFP?.permission_in_context !== FeaturePermission.Everyone
  )

  // Logic for view permission:
  // Show IF school View CFP is active (set to EVERYONE) OR
  // (user View CFP exists (set to EVERYONE) AND Preview CFP includes target User)
  const canView = Boolean(
    (hasPreviewPermission &&
      userScopedCFP?.permission_in_context === FeaturePermission.Everyone) ||
      schoolScopedCFP?.permission_in_context === FeaturePermission.Everyone
  )

  return {
    canView,
    canPreview,
    schoolScopedCFP: schoolScopedCFP || null,
    userScopedCFP: userScopedCFP || null,
    previewCFP: previewCFP || null
  }
}

// Via client.query.
export async function getUnifiedHomePermissions(
  pwContext: ContextFragment
): Promise<Permissions> {
  const client = getApolloClient() as ApolloClient<any>
  const [featureViewPermQuery, featurePreviewPermQuery] = await Promise.all([
    client.query<
      ContextualFeaturePermissionsQuery,
      ContextualFeaturePermissionsQueryVariables
    >({
      query: ContextualFeaturePermissionsDocument,
      variables: {
        ...unifiedHomeViewVariables,
        // Scoping to user, and since we're querying a list of CFPs, we'll get both the school-scoped
        // cfp (if it exists) and the user-scoped cfp (if it exists).
        context: {
          user_id: pwContext.me?.id
        }
      }
    }),
    await client.query<
      ContextualFeaturePermissionQuery,
      ContextualFeaturePermissionQueryVariables
    >({
      query: ContextualFeaturePermissionDocument,
      variables: unifiedHomePreviewVariables
    })
  ])

  return getPermissions(
    featureViewPermQuery,
    featurePreviewPermQuery,
    pwContext
  )
}

function useCanViewUnifiedHomeFeature() {
  const pwContext = usePathwrightContext()
  const featurePermQuery = useContextualFeaturePermissionsQuery({
    variables: {
      ...unifiedHomeViewVariables,
      // Scoping to user, and since we're querying a list of CFPs, we'll get both the school-scoped
      // cfp (if it exists) and the user-scoped cfp (if it exists).
      context: {
        user_id: pwContext.me?.id
      }
    }
  })

  return featurePermQuery
}

function useCanPreviewUnifiedHomeFeature() {
  const featurePermQuery = useContextualFeaturePermissionQuery({
    variables: unifiedHomePreviewVariables
  })

  return featurePermQuery
}

export function useHasUnifiedHomePath() {
  const { getPath, path } = useHomeActiveEntries()
  // Check if the current path is the unified home path.
  const hasUnifiedHomePath = path === getPath("paths.unified")
  return hasUnifiedHomePath
}

// Via hook.
export function useUnifiedHomePermissions(): GetPermissionsReturn & {
  refetchPermissions: () => Promise<void>
} {
  const pwContext = usePathwrightContext()
  const featureViewPermQuery = useCanViewUnifiedHomeFeature()
  const featurePreviewPermQuery = useCanPreviewUnifiedHomeFeature()
  const permissions = getPermissions(
    featureViewPermQuery,
    featurePreviewPermQuery,
    pwContext
  )
  const hasUnifiedPath = useHasUnifiedHomePath()

  async function refetchPermissions() {
    await Promise.all([
      featureViewPermQuery.refetch(),
      featurePreviewPermQuery.refetch()
    ])
  }

  return {
    ...permissions,
    canView:
      permissions.canView !== null
        ? permissions.canView || hasUnifiedPath
        : null,
    canPreview: permissions.canPreview,
    refetchPermissions
  }
}

// Simplified nav check.
export function useCanViewSimpleNav() {
  const { canView } = useUnifiedHomePermissions()
  const hasUnifiedPath = useHasUnifiedHomePath()

  // Show new nav when user can view unified home, or when explicitly on /home/unified/ to force it.
  return canView !== null ? canView || hasUnifiedPath : null
}

export default useUnifiedHomePermissions
